import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {AdminService} from '../../../../services/admin.service';
import {StandardAssignee, StandardFamiliarizationUser} from 'app/models/standard-user.model';
import {isStandardVersionConflict, savedStandardVersion, StandardVersionSnapshot} from 'app/services/standard-version';

const NO_USER_ERROR = '';
const HTTP_OK = 200;
const USER_ASSIGNMENT_ERROR = {conflict: 'STANDARD_VERSION_CONFLICT_MESSAGE', failed: 'STANDARD_ASSIGNMENT_SAVE_FAILED', load: 'STANDARD_ASSIGNMENT_RELOAD_FAILED'};

@Component({
  selector: 'app-add-user-steps',
  templateUrl: './add-user-steps.component.html',
  styleUrls: ['./add-user-steps.component.css']
})
export class AddUserStepsComponent implements OnChanges, OnDestroy {
  @Input() standard: StandardVersionSnapshot;
  @Input() blocked = false;
  @Output() usersChanged = new EventEmitter<StandardFamiliarizationUser[]>();
  @Output() reloadRequired = new EventEmitter<string>();
  @Output() reloadRequested = new EventEmitter<void>();
  selectedUser: StandardAssignee = null;
  lastAddedUser: StandardAssignee = null;
  allUsers: StandardAssignee[] = [];
  addedUsers: StandardAssignee[] = [];
  successInfo = false;
  pending = false;
  requiresReload = false;
  loadFailed = false;
  errorKey = NO_USER_ERROR;
  private snapshot: StandardVersionSnapshot;
  private sourceVersion: number;
  private cancelled$ = new Subject<void>();

  constructor(private adminService: AdminService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.standard || !this.standard) { return; }
    this.cancelled$.next();
    this.snapshot = Object.assign({}, this.standard);
    this.sourceVersion = this.standard.version;
    this.pending = this.requiresReload = this.successInfo = false;
    this.addedUsers = [];
    this.allUsers = [];
    this.selectedUser = null;
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.cancelled$.next();
    this.cancelled$.complete();
  }

  loadUsers(): void {
    if (this.pending || !this.snapshot) { return; }
    this.pending = true;
    this.loadFailed = false;
    this.errorKey = NO_USER_ERROR;
    this.adminService.getUsersOusideStandard(this.snapshot.id).takeUntil(this.cancelled$).subscribe(
      (response: HttpResponse<StandardAssignee[]>) => {
        this.pending = false;
        this.allUsers = [null].concat((response.body || []).slice().sort((left, right) => left.firstName.localeCompare(right.firstName)));
      }, () => { this.pending = false; this.loadFailed = true; this.errorKey = USER_ASSIGNMENT_ERROR.load; }
    );
  }

  selectUser(user: StandardAssignee): void {
    if (this.pending || this.requiresReload || this.blocked) { return; }
    this.successInfo = false;
    this.selectedUser = user;
  }

  addUserToStandard(): void {
    if (this.pending || this.requiresReload || this.blocked || !this.selectedUser) { return; }
    const user = this.selectedUser;
    this.pending = true;
    this.successInfo = false;
    this.errorKey = NO_USER_ERROR;
    this.adminService.addUserToStandard(this.snapshot, user.id).takeUntil(this.cancelled$).subscribe(
      response => this.acceptUser(user, response), error => this.rejectUser(error)
    );
  }

  requestReload(): void { if (!this.pending) { this.reloadRequested.emit(); } }

  trackUser(index: number, user: StandardAssignee): string { return user && user.id; }

  private acceptUser(user: StandardAssignee, response: HttpResponse<StandardFamiliarizationUser[]>): void {
    this.pending = false;
    if (response.status !== HTTP_OK || !Array.isArray(response.body)) { this.requireReload(USER_ASSIGNMENT_ERROR.failed); return; }
    this.successInfo = true;
    this.addedUsers.push(user);
    this.lastAddedUser = user;
    this.allUsers = this.allUsers.filter(item => !item || item.id !== user.id);
    this.selectedUser = null;
    const version = savedStandardVersion(response);
    if (this.standard.version === this.sourceVersion && version != null) {
      this.standard.version = this.sourceVersion = version;
    } else {
      this.requireReload(version == null ? USER_ASSIGNMENT_ERROR.failed : USER_ASSIGNMENT_ERROR.conflict);
    }
    this.usersChanged.emit(response.body);
  }

  private rejectUser(error: HttpErrorResponse): void {
    this.pending = false;
    this.requireReload(isStandardVersionConflict(error) ? USER_ASSIGNMENT_ERROR.conflict : USER_ASSIGNMENT_ERROR.failed);
  }

  private requireReload(errorKey: string): void {
    this.requiresReload = true;
    this.errorKey = errorKey;
    this.reloadRequired.emit(errorKey);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/create-operating-standard/add-user-steps/add-user-steps.component.ts